'use client';

import {
	createContext,
	useContext,
	useState,
	useEffect,
	useRef,
	useCallback,
	useMemo,
	ReactNode,
} from 'react';
import { UserHierarchyLevel } from '@practium/shared';
import type { BindingUserApiResponse } from '../../users/types';
import api from '../../api/http';

interface ForBindingContextType {
	forBindingUser: BindingUserApiResponse | null;
	forBindingId: string | null;
	forBindingUserLevel: UserHierarchyLevel | null;
	isForBinding: boolean;
	exitProfile: () => void;
	forBindingParams: { forBinding: string } | Record<string, never>;
	refreshForBindingUser: () => Promise<void>;
}

const ForBindingContext = createContext<ForBindingContextType | undefined>(undefined);

const SESSION_KEY = 'practium:forBinding';

interface ForBindingSession {
	forBindingId: string;
	bindingType: number;
}

function saveSession(data: ForBindingSession): void {
	sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
}

function loadSession(): ForBindingSession | null {
	try {
		const raw = sessionStorage.getItem(SESSION_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function clearSession(): void {
	sessionStorage.removeItem(SESSION_KEY);
}

function bindingTypeToLevel(bindingType: number): UserHierarchyLevel {
	switch (bindingType) {
		case 1:
			return UserHierarchyLevel.Farmer;
		case 2:
			return UserHierarchyLevel.Consultant;
		case 3:
			return UserHierarchyLevel.Company;
		default:
			return UserHierarchyLevel.Farmer;
	}
}

function clearForBindingFromUrl(): void {
	const url = new URL(globalThis.window.location.href);
	url.searchParams.delete('forBinding');
	url.searchParams.delete('bindingType');
	globalThis.window.history.replaceState({}, '', url.toString());
}

interface ForBindingProviderProps {
	children: ReactNode;
	currentUserId: string | null;
}

export const ForBindingProvider = ({ children, currentUserId }: ForBindingProviderProps) => {
	const [forBindingId, setForBindingId] = useState<string | null>(null);
	const [forBindingUser, setForBindingUser] = useState<BindingUserApiResponse | null>(null);
	const [bindingType, setBindingType] = useState<number | null>(null);
	const forBindingIdRef = useRef<string | null>(null);
	const bindingTypeRef = useRef<number | null>(null);
	const initialized = useRef(false);

	const urlCaptured = useRef(false);
	if (!urlCaptured.current) {
		urlCaptured.current = true;
		const params = new URLSearchParams(globalThis.window.location.search);
		const id = params.get('forBinding');
		if (id) {
			const session = {
				forBindingId: id,
				bindingType: Number(params.get('bindingType')) || 1,
			};
			saveSession(session);
			clearForBindingFromUrl();
		}
	}

	useEffect(() => {
		if (initialized.current) return;
		if (currentUserId === null) return;

		initialized.current = true;

		const session = loadSession();
		const targetId = session?.forBindingId;
		const bindingType = session?.bindingType ?? 1;

		if (!targetId) return;

		if (targetId === currentUserId) {
			clearSession();
			return;
		}

		api
			.get<BindingUserApiResponse>(`/binding-user/findOne/${bindingType}/${targetId}`)
			.then(({ data: bindingUser }) => {
				if (!bindingUser) {
					clearSession();
					return;
				}

				forBindingIdRef.current = targetId;
				bindingTypeRef.current = bindingType;
				setForBindingId(targetId);
				setForBindingUser(bindingUser);
				setBindingType(bindingType);
			})
			.catch(() => {
				clearSession();
			});
	}, [currentUserId]);

	const refreshForBindingUser = useCallback(async () => {
		if (!forBindingIdRef.current || bindingTypeRef.current === null) return;

		const { data: bindingUser } = await api.get<BindingUserApiResponse>(
			`/binding-user/findOne/${bindingTypeRef.current}/${forBindingIdRef.current}`
		);

		if (bindingUser) setForBindingUser(bindingUser);
	}, []);

	const exitProfile = useCallback(() => {
		forBindingIdRef.current = null;
		bindingTypeRef.current = null;
		setForBindingId(null);
		setForBindingUser(null);
		setBindingType(null);
		clearForBindingFromUrl();
		clearSession();
	}, []);

	const forBindingParams = useMemo(
		() =>
			forBindingIdRef.current
				? { forBinding: forBindingIdRef.current }
				: ({} as Record<string, never>),
		[forBindingId]
	);

	const forBindingUserLevel = useMemo(
		() => (bindingType === null ? null : bindingTypeToLevel(bindingType)),
		[bindingType]
	);

	const originalTitleRef = useRef<string | null>(null);
	useEffect(() => {
		if (originalTitleRef.current === null) {
			originalTitleRef.current = document.title;
		}

		document.title = forBindingUser
			? `${forBindingUser.name} ${forBindingUser.surName} · ${originalTitleRef.current}`
			: originalTitleRef.current;
	}, [forBindingUser]);

	const value = useMemo(
		() => ({
			forBindingUser,
			forBindingId,
			forBindingUserLevel,
			isForBinding: !!forBindingIdRef.current,
			exitProfile,
			forBindingParams,
			refreshForBindingUser,
		}),
		[
			forBindingUser,
			forBindingId,
			forBindingUserLevel,
			exitProfile,
			forBindingParams,
			refreshForBindingUser,
		]
	);

	return <ForBindingContext.Provider value={value}>{children}</ForBindingContext.Provider>;
};

export const useForBinding = () => {
	const context = useContext(ForBindingContext);
	if (!context) {
		throw new Error('useForBinding deve ser usado dentro do ForBindingProvider');
	}
	return context;
};

export const useForBindingId = (): string | undefined => {
	const context = useContext(ForBindingContext);
	return context?.forBindingId ?? undefined;
};
